import { constants } from './constants';
import PptxGenJS from 'pptxgenjs';
import { SlideWithCurrentPositionY, TextPpt } from './interfaces';
import {
  textProposToTextPpt,
  createTextOptions,
  calculateTextHeightPerFragment,
} from './helpers/textHelpers';

// Helper function to detect if text starts with a bullet or numbered list item
const isBulletOrNumberedItemText = (text: string): boolean => {
  const trimmed = text.trim();
  // Check for bullet point (•, ◦, etc.)
  if (/^[•◦○■▪]/u.test(trimmed)) return true;
  // Check for markdown format bullets (- , * , + )
  if (/^[-*+]\s/.test(trimmed)) return true;
  // Check for numbered list (1., 2., etc.)
  if (/^\d+\.\s/.test(trimmed)) return true;
  return false;
};

export const addTextToSlide = (
  pptx: PptxGenJS,
  textObjects: PptxGenJS.TextProps[],
  position: { x: number; y: number },
  availableWidth: number,
  slide: PptxGenJS.Slide
): SlideWithCurrentPositionY => {
  if (!textObjects || textObjects.length === 0) {
    return { slide, positionY: position.y };
  }

  const textPpt: TextPpt[] = textProposToTextPpt(textObjects);
  const maxHeight = constants.SLIDE_DIMENSION_Y - constants.BOTTOM_MARGIN;

  let currentSlide = slide;
  let currentY = position.y;
  let currentLine: TextPpt[] = [];
  let bulletLineStart = -1; // Track the start of a bullet line

  for (let i = 0; i < textPpt.length; i++) {
    const fragment = textPpt[i];
    const previousHeight = calculateTextHeightPerFragment(currentLine, availableWidth, position.x);

    currentLine.push(fragment);
    
    // Check if this fragment starts a bullet/numbered item
    if (fragment.text && /^([-*+]|\d+\.|[•◦○■▪])/.test(fragment.text.trim())) {
      bulletLineStart = currentLine.length - 1;
    }

    const newHeight = calculateTextHeightPerFragment(currentLine, availableWidth, position.x);

    const overflowAllowance = 0.6;
    if (currentY + newHeight > maxHeight + overflowAllowance) {
      currentLine.pop();

      // If we're in the middle of a bullet line, remove the entire bullet line
      if (bulletLineStart !== -1 && bulletLineStart < currentLine.length) {
        currentLine.splice(bulletLineStart);
      }

      if (currentLine.length > 0) {
        const textOptions = createTextOptions(
          { x: position.x, y: currentY },
          availableWidth,
          previousHeight,
          currentLine[0].fontSize ?? constants.DEFAULT_FONT_SIZE
        );
        currentSlide.addText(currentLine, textOptions);
      }

      currentSlide = pptx.addSlide();
      currentY = constants.INITIAL_POSITION_Y + constants.TEXT_MARGIN;
      currentLine = [fragment];
      bulletLineStart = -1; // Reset for new slide
      
      // If fragment itself starts a bullet, mark it
      if (fragment.text && /^([-*+]|\d+\.|[•◦○■▪])/.test(fragment.text.trim())) {
        bulletLineStart = 0;
      }
    }
  }

  if (currentLine.length > 0) {
    const finalHeight = calculateTextHeightPerFragment(currentLine, availableWidth, position.x);
    const fontSize = currentLine[0].fontSize ?? constants.DEFAULT_FONT_SIZE;
    const textOptions = createTextOptions({ x: position.x, y: currentY }, availableWidth, finalHeight, fontSize);
    currentSlide.addText(currentLine, textOptions);
    currentY += finalHeight;
  }

  return { slide: currentSlide, positionY: currentY };
};